/**
 * Lead Completeness Indicator Component - SOP #1
 * Displays qualification status and missing fields
 */

import React, { useState } from "react";
import { Badge, Popover, Button, Space, Typography } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { checkQualification } from "../services/leadQualificationService";

const { Text } = Typography;

/**
 * Lead Completeness Indicator
 * @param {Object} props - Component props
 * @param {Object} props.lead - Lead object
 * @param {Function} props.onCheck - Callback when checking completeness
 */
const LeadCompletenessIndicator = ({ lead, onCheck }) => {
  const [loading, setLoading] = useState(false);
  const [completeness, setCompleteness] = useState(
    lead?.qualification?.completeness || null
  );

  const handleCheck = async () => {
    if (!lead?.id) return;

    setLoading(true);
    try {
      const result = await checkQualification(lead.id);
      setCompleteness(result.completeness);
      if (onCheck) {
        onCheck(result);
      }
    } catch (error) {
      console.error("Error checking completeness:", error);
    } finally {
      setLoading(false);
    }
  };

  const isQualified = completeness?.qualifiedForQuoting || false;
  const missingFields = completeness?.missingFields || [];

  const content = (
    <div>
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <div>
          <Text strong>Status: </Text>
          <Text type={isQualified ? "success" : "warning"}>
            {isQualified ? "Qualified" : "Not Qualified"}
          </Text>
        </div>
        {missingFields.length > 0 && (
          <div>
            <Text strong>Missing Fields:</Text>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              {missingFields.map((field, index) => (
                <li key={index}>
                  <Text type="warning">{field}</Text>
                </li>
              ))}
            </ul>
          </div>
        )}
        <Button
          type="link"
          size="small"
          onClick={handleCheck}
          loading={loading}
          style={{ padding: 0 }}
        >
          Refresh Status
        </Button>
      </Space>
    </div>
  );

  return (
    <Popover content={content} title="Qualification Status" trigger="click">
      <Badge
        status={isQualified ? "success" : "warning"}
        text={
          <Space>
            {isQualified ? (
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
            ) : (
              <ExclamationCircleOutlined style={{ color: "#faad14" }} />
            )}
            <Text strong={!isQualified}>
              {isQualified ? "Qualified" : "Not Qualified"}
            </Text>
            {missingFields.length > 0 && (
              <Text type="secondary">({missingFields.length} missing)</Text>
            )}
          </Space>
        }
      />
    </Popover>
  );
};

export default LeadCompletenessIndicator;
